import { useEffect, useRef, useState } from 'react';
import styles from './index.module.less';
import { useTranslation } from 'next-i18next';
import { Loader2 } from 'lucide-react';
import { emailVerificationRegister, sendEmailVerificationCode } from '@/lib/service/user';
import { message } from 'antd';
import { isDevelopment } from '@/utils/utils';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import { useUserProvider } from '@/context/UserProvider';

const LoginModalEmailIcon = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M20.333 4C21.253 4 22 4.746 22 5.667V17.333C22 18.253 21.254 19 20.333 19H3.667C2.747 19 2 18.254 2 17.333V5.667C2 4.747 2.746 4 3.667 4H20.333ZM9.168 12.828L4.258 17.68H19.583L14.726 12.878L12 14.765L9.168 12.828ZM20.64 8.792L15.844 12.108L20.64 16.844V8.792ZM3.361 8.853V16.689L8.047 12.058L3.361 8.853ZM20.317 5.321H3.698C3.60675 5.31672 3.51731 5.34749 3.448 5.407C3.4172 5.43458 3.39343 5.46911 3.37864 5.50772C3.36386 5.54633 3.3585 5.5879 3.363 5.629V7.249L12 13.156L20.644 7.191V5.699C20.645 5.565 20.614 5.468 20.552 5.407C20.5212 5.37705 20.4845 5.35391 20.4442 5.33909C20.4038 5.32427 20.3609 5.31811 20.318 5.321H20.317Z"
      fill="#387AFF"
    />
  </svg>
);

interface Props {
  formData: any;
  onBackToRegister: () => void;
  onSuccess: () => void;
}

// 本地开发环境 10 秒，测试和生产环境 60 秒
const COUNTDOWN_TIME = isDevelopment ? 10 : 60;

export default function EmailRegisterInput({ formData, onBackToRegister, onSuccess }: Props) {
  const { t } = useTranslation();
  const { onAddToken } = useUserProvider();
  const [verificationCode, setVerificationCode] = useState('');
  const [countdown, setCountdown] = useState(COUNTDOWN_TIME); // 倒计时状态，初始值即为倒计时秒数，组件挂载时自动开始
  const countdownRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 重发验证码
  const [isResendCodeLoading, setIsResendCodeLoading] = useState(false);
  // 注册 loading
  const [isRegisterLoading, setIsRegisterLoading] = useState(false);

  const handleSubmit = async (newValue?: string) => {
    const { email, password } = formData;
    const code = newValue || verificationCode;
    if (!email || !password || code.length !== 6) return;
    setIsRegisterLoading(true);
    emailVerificationRegister(email, code, password, localStorage.getItem('source'))
      .then((res) => {
        if (res.code == 0) {
          onAddToken(res.data.token, 'email');
          localStorage.removeItem('source');
          onSuccess();
          message.success(t('Register successfully'));
          ga_logEvent(EventName.user_register_Success, { method: 'Email' });
          ga_logEvent(EventName.login, { method: 'Email' });
        } else {
          message.error(res.message);
        }
      })
      .catch((error) => {
        message.error(error.message);
      })
      .finally(() => {
        setIsRegisterLoading(false);
      });
  };

  // 处理重新发送验证码
  const handleResendCode = async () => {
    if (countdown > 0) return; // 如果倒计时还在进行中，不允许重新发送
    setIsResendCodeLoading(true);
    sendEmailVerificationCode(formData?.email, 'register')
      .then((res) => {
        if (res.code == 0) {
          startCountdown(); // 重新启动倒计时
          message.success(t('Please enter the 6-digit verification code sent to your email.'));
        } else {
          message.error(res.message);
        }
      })
      .catch((error) => {
        message.error(error.message);
      })
      .finally(() => {
        setIsResendCodeLoading(false);
      });
  };

  // 倒计时效果
  useEffect(() => {
    if (countdown > 0) {
      countdownRef.current = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdownRef.current) {
      clearTimeout(countdownRef.current);
      countdownRef.current = null;
    }

    return () => {
      if (countdownRef.current) {
        clearTimeout(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [countdown]);

  // 组件卸载时清理倒计时
  useEffect(() => {
    return () => {
      if (countdownRef.current) {
        clearTimeout(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, []);

  // 启动倒计时的函数
  const startCountdown = () => {
    setCountdown(COUNTDOWN_TIME);
  };

  return (
    <div className={styles.container}>
      <div className={styles.titleContainer}>{t('Verify your Email')}</div>
      <div className={styles.backToRegister}>
        <span onClick={onBackToRegister}>{`← ${t('Back to Email entry')}`}</span>
      </div>
      <div className={styles.verifySection}>
        <div className={styles.verifyContent}>
          <div className={styles.verifyText}>{t('Enter the verification code we sent to')}:</div>
          <div className={styles.emailBox}>
            {LoginModalEmailIcon}
            <span className={styles.emailText}>{formData?.email}</span>
          </div>
        </div>

        <div
          className={styles.codeInputContainer}
          onPaste={(e) => {
            e.preventDefault();
            const pastedData = e.clipboardData.getData('text').trim();
            const numbers = pastedData.replace(/\D/g, '').slice(0, 6);
            setVerificationCode(numbers.padEnd(6, ''));

            // 如果粘贴了完整的6位数字，将聚焦到最后一个输入框
            if (numbers.length === 6) {
              const inputs = e.currentTarget.querySelectorAll('input');
              inputs[5].focus();
            }
          }}
        >
          {Array(6)
            .fill(0)
            .map((_, index) => (
              <input
                key={index}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                className={styles.codeInput}
                value={verificationCode[index] || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  if (value || value === '') {
                    const newCode = verificationCode.split('');
                    newCode[index] = value.slice(-1);
                    const newValue = newCode.join('');
                    setVerificationCode(newValue);

                    // 自动跳转到下一个输入框
                    if (value && index < 5) {
                      const inputs = (e.target as HTMLElement).closest('div')?.querySelectorAll('input');
                      if (inputs?.[index + 1]) {
                        inputs[index + 1].focus();
                      }
                    }

                    // 如果是最后一个框且已填满6位数字，触发验证
                    if (index === 5 && newValue.length === 6) {
                      handleSubmit(newValue);
                    }
                  }
                }}
                onKeyDown={(e) => {
                  // 处理方向键
                  if (e.key === 'ArrowLeft' && index > 0) {
                    e.preventDefault();
                    const inputs = (e.target as HTMLElement).closest('div')?.querySelectorAll('input');
                    inputs?.[index - 1]?.focus();
                  }
                  if (e.key === 'ArrowRight' && index < 5) {
                    e.preventDefault();
                    const inputs = (e.target as HTMLElement).closest('div')?.querySelectorAll('input');
                    inputs?.[index + 1]?.focus();
                  }

                  // 处理退格键
                  if (e.key === 'Backspace') {
                    const newCode = verificationCode.split('');
                    if (newCode[index]) {
                      // 如果当前输入框有值，清空当前值
                      newCode[index] = '';
                      setVerificationCode(newCode.join(''));
                    } else if (index > 0) {
                      const inputs = (e.target as HTMLElement)?.closest('div')?.querySelectorAll('input');
                      if (inputs?.[index - 1]) {
                        newCode[index - 1] = '';
                        setVerificationCode(newCode.join(''));
                        inputs[index - 1].focus();
                      }
                    }
                  }

                  // 处理回车键
                  if (e.key === 'Enter' && verificationCode.length === 6) {
                    handleSubmit();
                  }
                }}
                onFocus={(e) => e.target.select()} // 聚焦时选中内容
              />
            ))}
        </div>

        <button
          type="submit"
          className={styles.submitBtn}
          onClick={() => handleSubmit()}
          disabled={verificationCode.length !== 6 || isRegisterLoading}
        >
          {isRegisterLoading ? (
            <span className={styles.loadingContent}>
              <Loader2 className={styles.loadingIcon} />
              {t('Verifying...')}
            </span>
          ) : (
            t('Verify')
          )}
        </button>

        <button disabled={isResendCodeLoading || countdown > 0} className={styles.resendBtn}>
          {isResendCodeLoading ? (
            <span className={styles.resendContent}>
              <Loader2 className={styles.loadingIcon} />
              {t('Sending...')}
            </span>
          ) : countdown > 0 ? (
            <span>{`${t('Resend code in')} ${countdown}s`}</span>
          ) : (
            <span onClick={handleResendCode}>{t('Resend code')}</span>
          )}
        </button>
      </div>
    </div>
  );
}
